import { Box, Card, Stack, Typography } from "@mui/material";
import { green, red } from "@mui/material/colors";
import { memo, useEffect, useRef } from "react";
import {
  formatMegawatts,
  kilowattsToMegawatts,
  QUANTITY_UNIT,
} from "../../megawatts";
import { find } from "../orderBook/allGrids";
import { useSelectedOrderBookSnapshot } from "./queries";
import { useVenuePriceDelimiter } from "./useVenuePriceDelimiter";
import { PublicOrder } from "./types";
import { Direction as TradeDirection } from "../lazertrader/types";
import { useListedAlgo } from "../lazertrader/contexts/listAlgosContext";
import { usePrefillContext } from "../../Services/PrefillContextProvider";
import { formatYenPerKwh, Price, PRICE_UNIT } from "../../price";

type Side = "bid" | "ask";

const sideColor = (side: Side) => (side === "bid" ? green[400] : red[400]);

const LADDER_WIDTH = 120;

const cellSx = { flex: 1 } as const;

const Empty = ({ message }: { message: string }) => (
  <Stack
    sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}
  >
    <Typography sx={{ color: "text.disabled" }}>{message}</Typography>
  </Stack>
);

const DepthRow = memo(
  ({
    order,
    color,
    delimiter,
    side,
  }: {
    order: PublicOrder;
    color: string;
    delimiter: number;
    side: Side;
  }) => {
    const isOwn = order.is_own === "Own";
    const mw = formatMegawatts(kilowattsToMegawatts(order.quantity.kilo_watts));
    const price = Price.fromScaledMwh(order.price.price, delimiter);

    const { updatePrefill, prefill, selectedContract } = usePrefillContext();

    const clickPrice = () => {
      if (prefill) {
        const updatedPrefill = {
          ...prefill,
          // The area this ladder shows, which is the one picked in the config
          // tab — `prefill.grid` is still the area first clicked in the order
          // book, and re-sending it would switch the form (and the reference
          // price taken from that area's spot) back.
          grid: selectedContract?.areaCode ?? prefill.grid,
          // The side that trades against the price you clicked, matching the
          // order book grid: you sell into a bid and buy from an ask. Keyed off
          // the ladder the row is in rather than `order.direction` — that is
          // the resting order's own side, which is the opposite of yours.
          direction: side === "bid" ? TradeDirection.SELL : TradeDirection.BUY,
          price,
        };

        updatePrefill(updatedPrefill);
      }
    };

    return (
      <Stack
        direction={side === "bid" ? "row-reverse" : "row"}
        sx={{
          gap: 1,
          paddingRight: 1,
          paddingLeft: "6px",
          paddingY: "1px",
          justifyContent: "space-between",
          fontWeight: isOwn ? 700 : 400,
          borderLeft: "2px solid",
          borderLeftColor: isOwn ? "primary.main" : "transparent",
          backgroundColor: isOwn
            ? (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.04)"
            : "transparent",
        }}
      >
        <Box
          component="span"
          // Blue for our own level, as the order book grid paints an own top of
          // book (`orderBook/quoteCell.tsx`); the side colour otherwise.
          color={isOwn ? "primary.main" : color}
          sx={{
            cursor: "pointer",
            // `equals`, not `===`: Price is a class, so identity would never
            // match and the highlight would simply never appear.
            textDecoration: price.equals(prefill?.price)
              ? "underline"
              : "none",
          }}
          onClick={clickPrice}
        >
          {formatYenPerKwh(price)}
        </Box>
        <Box component="span" sx={{ color: "warning.main" }}>
          {mw ?? "-"}
        </Box>
      </Stack>
    );
  }
);

DepthRow.displayName = "DepthRow";

const SideLadder = ({
  side,
  orders,
  delimiter,
}: {
  side: Side;
  orders: PublicOrder[];
  delimiter: number;
}) => {
  const color = sideColor(side);

  return (
    <Stack sx={{ width: LADDER_WIDTH, minHeight: 0, flexShrink: 0 }}>
      <Typography sx={{ fontWeight: 700, color, paddingLeft: "6px" }}>
        {side === "bid" ? "Bids" : "Asks"}
      </Typography>
      <Stack
        direction="row"
        sx={{
          gap: 1,
          paddingRight: 1,
          paddingLeft: "8px",
          color: "text.secondary",
          fontSize: "0.9em",
          flexShrink: 0,
        }}
      >
        {side === "bid" ? (
          <>
            <Box component="span" sx={cellSx} title={`Quantity in ${QUANTITY_UNIT}`}>
              {QUANTITY_UNIT}
            </Box>
            <Box
              component="span"
              sx={{ ...cellSx, textAlign: "right" }}
              title={`Price in ${PRICE_UNIT}`}
            >
              Price
            </Box>
          </>
        ) : (
          <>
            <Box component="span" sx={cellSx} title={`Price in ${PRICE_UNIT}`}>
              Price
            </Box>
            <Box
              component="span"
              sx={{ ...cellSx, textAlign: "right" }}
              title={`Quantity in ${QUANTITY_UNIT}`}
            >
              {QUANTITY_UNIT}
            </Box>
          </>
        )}
      </Stack>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto" }}>
        {orders.length === 0 ? (
          <Typography sx={{ color: "text.disabled", paddingLeft: "8px" }}>
            —
          </Typography>
        ) : (
          orders.map((order, index) => (
            <DepthRow
              // Price and size do not uniquely identify a level, and the payload
              // carries no order id, so position in the book is the only key.
              key={`${order.price.price}-${order.quantity.kilo_watts}-${index}`}
              order={order}
              color={color}
              delimiter={delimiter}
              side={side}
            />
          ))
        )}
      </Box>
    </Stack>
  );
};

const FullDepthOrderbookComponent = () => {
  const { snapshot, loading, error, selectedContract } =
    useSelectedOrderBookSnapshot();

  const delimiter = useVenuePriceDelimiter();

  const bids = snapshot?.bids ?? [];
  const asks = snapshot?.asks ?? [];

  const emptyMessage =
    selectedContract === undefined
      ? "Click a bid or ask to see its depth"
      : error
        ? "Could not load depth"
        : loading
          ? "Loading…"
          : undefined;

  const { addPreventClearAlgosRef, removePreventClearAlgosRef } =
    useListedAlgo();

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    addPreventClearAlgosRef(ref);
    return () => removePreventClearAlgosRef(ref);
  }, [addPreventClearAlgosRef, removePreventClearAlgosRef]);

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
      ref={ref}
    >
      <Card
        elevation={5}
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "baseline",
            gap: 1,
            paddingX: 1,
            paddingY: 0.5,
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Depth</Typography>
          {selectedContract && (
            <Typography sx={{ color: "text.secondary" }}>
              {find(selectedContract.areaCode).displayName}
            </Typography>
          )}
        </Stack>

        {emptyMessage !== undefined ? (
          <Box sx={{ flexGrow: 1, minHeight: 0 }}>
            <Empty message={emptyMessage} />
          </Box>
        ) : (
          <Stack
            direction="row"
            sx={{ flexGrow: 1, minHeight: 0, gap: 2, paddingTop: 0.5 }}
          >
            <SideLadder side="bid" orders={bids} delimiter={delimiter} />
            <Box sx={{ borderLeft: (t) => `1px solid ${t.palette.divider}` }} />
            <SideLadder side="ask" orders={asks} delimiter={delimiter} />
          </Stack>
        )}
      </Card>
    </Box>
  );
};

export const FullDepthOrderbook = memo(FullDepthOrderbookComponent);

FullDepthOrderbook.displayName = "FullDepthOrderbook";
